// CPP-tester results-table columns.
//
// Reuses the Ads Manager column registry (`ALL_COLUMNS`) so the picker
// offers "the same columns as Ad Manager" (performance + enriched +
// conversions + cohort — the metric categories; structural "parameters"
// like daily_budget / bid don't apply to a single CPP variant). Plus the
// three ratio columns Ads Manager computes inline in MetricCells (not
// ColumnKeys): IPM / CPM / CPI — core to a CPP comparison.
//
// Column keys are plain strings here (a superset of ColumnKey). Presets
// store them verbatim; the backend doesn't validate keys, so adding more
// later needs no migration.

import { ALL_COLUMNS } from "@/lib/columns-config";

import type { CppVariantSnapshot } from "./types";

// Ratio columns not present in ALL_COLUMNS (Ads Manager derives them in
// MetricCells). Surfaced under Performance for the tester.
const TESTER_EXTRA: { key: string; label: string }[] = [
  { key: "ipm", label: "IPM (Installs Per Mille)" },
  { key: "cpm", label: "CPM" },
  { key: "cpi", label: "CPI (Cost Per Install)" },
];

export type CppColCat = {
  id: string;
  label: string;
  items: { key: string; label: string }[];
};

// Performance keys that actually compute per CPP variant. Tap/View install
// splits, CPA(Tap/View), CR(Tap), new/redownload splits, impression-share
// and SOV aren't tracked per variant, so they're excluded — every column
// the tester offers must show real data.
const PERF_WHITELIST = [
  "impressions",
  "taps",
  "ttr",
  "avg_cpt",
  "spend",
  "installs",
  "installs_total",
  "cr_total",
  "cpa",
];

/** Categories for the Edit-columns modal. Performance is a computable
 *  whitelist (+ IPM/CPM/CPI); Conversions + Cohort come from the real Ads
 *  Manager registry (all compute per variant via the backend `extra`
 *  map). Order matches the design (Performance → Conversions → Cohort). */
export const CPP_COL_CATS: CppColCat[] = (() => {
  const labelOf = (k: string) =>
    ALL_COLUMNS.find((c) => c.key === k)?.label ?? k;
  const perf: { key: string; label: string }[] = [
    ...TESTER_EXTRA,
    ...PERF_WHITELIST.map((k) => ({ key: k, label: labelOf(k) })),
  ];
  const conv: { key: string; label: string }[] = [];
  const cohort: { key: string; label: string }[] = [];
  for (const c of ALL_COLUMNS) {
    if (c.category === "conversions") conv.push({ key: c.key, label: c.label });
    else if (c.category === "cohort") cohort.push({ key: c.key, label: c.label });
  }
  return [
    { id: "performance", label: "Performance", items: perf },
    { id: "conversions", label: "Conversions", items: conv },
    { id: "cohort", label: "Cohort", items: cohort },
  ];
})();

export const CPP_COL_LABEL: Record<string, string> = (() => {
  const m: Record<string, string> = {};
  for (const c of ALL_COLUMNS) m[c.key] = c.label;
  for (const e of TESTER_EXTRA) m[e.key] = e.label;
  return m;
})();

/** All selectable keys (for validation / "show count"). */
export const CPP_ALL_COL_KEYS: string[] = CPP_COL_CATS.flatMap((c) =>
  c.items.map((i) => i.key),
);

/** Keys the tester is allowed to render. Stale keys from old saved
 *  presets / localStorage (e.g. tap/view splits removed from the picker)
 *  are filtered out at render so they never show as "—" columns. */
const CPP_ALLOWED = new Set(CPP_ALL_COL_KEYS);
export function cppFilterCols(cols: string[]): string[] {
  return cols.filter((k) => CPP_ALLOWED.has(k));
}

/** Fallback visible set when the app has no default preset. The Fact /
 *  Scaled columns already carry the test's primary metric, so the extras
 *  stay modest. */
export const CPP_DEFAULT_COLS: string[] = [
  "installs",
  "taps",
  "spend",
  "cpa",
];

export function cppColLabel(key: string): string {
  return CPP_COL_LABEL[key] ?? key;
}

const fmtInt = (n: number) => Math.round(n).toLocaleString("en-US");
const money = (n: number) => "$" + n.toFixed(2);

/** Per-variant value for a column key. Performance + the IPM/CPM/CPI/ROAS
 *  ratios are computed from the snapshot; conversions + cohort columns
 *  render "—" until the per-variant rollup carries them (Phase 1c/2). */
export function cppColValue(v: CppVariantSnapshot, key: string): string {
  const { impressions: impr, taps, installs: inst, spend_usd: spend } = v;
  const rev = v.revenue_usd;
  switch (key) {
    case "impressions":
      return fmtInt(impr);
    case "taps":
      return fmtInt(taps);
    case "installs":
    case "installs_total":
      return fmtInt(inst);
    case "spend":
      return money(spend);
    case "revenue":
      return rev != null ? money(rev) : "—";
    case "ipm":
      return v.ipm != null
        ? v.ipm.toFixed(2)
        : impr > 0
          ? ((inst * 1000) / impr).toFixed(2)
          : "—";
    case "cpm":
      return impr > 0 ? money((spend * 1000) / impr) : "—";
    case "cpi":
      return v.cpi != null
        ? money(v.cpi)
        : inst > 0
          ? money(spend / inst)
          : "—";
    case "ttr":
      return impr > 0 ? ((taps * 100) / impr).toFixed(2) + "%" : "—";
    case "avg_cpt":
      return taps > 0 ? money(spend / taps) : "—";
    case "cr_total":
      // Conversion rate (tap → install): installs / taps.
      return taps > 0 ? ((inst / taps) * 100).toFixed(2) + "%" : "—";
    // Ads-Manager "Average CPA" / "(Total)" = spend / installs (the
    // average install cost). NOT the test's metric CPA (cost per the
    // chosen in-app action) — that's the dedicated Fact column. Tap/View
    // splits aren't tracked per variant, so they stay "—".
    case "cpa":
      return inst > 0 ? money(spend / inst) : "—";
    case "roas": {
      const r = v.roas ?? v.extra?.roas;
      return r != null ? r.toFixed(2) + "×" : "—";
    }
    default: {
      // Cohort-by-day + per-event conversions (Phase 2): values come from
      // the backend `extra` map, formatted by key suffix. tap/view CPA
      // splits + Adjust per-event have no value → "—".
      const x = v.extra?.[key];
      if (x == null) return "—";
      if (key.endsWith("_cr")) return x.toFixed(2) + "%";
      if (key.endsWith("_revenue") || key.endsWith("_cpa")) return money(x);
      if (key.startsWith("roas")) return x.toFixed(2) + "×";
      if (key.startsWith("arpu") || key.startsWith("revenue_d")) return money(x);
      if (key.startsWith("roi")) return x.toFixed(1) + "%";
      return fmtInt(x); // bare per-event count
    }
  }
}
